import { Box, Typography, Chip, Divider, IconButton } from '@mui/material'
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  FlightTakeoff as PickupIcon,
  FlightLand as DropIcon
} from '@mui/icons-material'
import { LoadDetails, getStatusColor } from '@/types/load'
import {
  formatCurrency,
  formatPhoneNumber,
  formatCustomerName,
  formatCarrierName,
  formatCommodity,
  formatTemperature,
  formatRateDetailsSummary,
  formatSalesPerson,
  calculateTotalRate
} from '@/utils/formatters'
import { getAllStopsSorted, formatStopSchedule } from '@/utils/stopUtils'
import { formatStopAddress } from '@/utils/addressFormat'

interface LoadSummaryProps {
  load: LoadDetails
}

const LoadSummary = ({ load }: LoadSummaryProps) => {
  // Get first pickup and last drop for summary
  const allStops = getAllStopsSorted(load.stops, load.emptyMoveStops)
  const regularStops = allStops.filter(s => !s.isEmptyMoveStop)
  const firstPickup = regularStops.find(
    s => s.stopType === 'pickup' || s.type === 'pickup'
  )
  const lastDrop = [...regularStops]
    .reverse()
    .find(s => s.stopType === 'drop' || s.type === 'drop')

  // Format first pickup info - using common utility (DRY)
  const firstPickupLocation = formatStopAddress(firstPickup) || null
  const firstPickupSchedule = firstPickup
    ? formatStopSchedule(firstPickup)
    : null

  // Format last drop info - using common utility (DRY)
  const lastDropLocation = formatStopAddress(lastDrop) || null
  const lastDropSchedule = lastDrop ? formatStopSchedule(lastDrop) : null

  // Format customer name using selectAsync pattern
  const customerDisplay = formatCustomerName(load.customer)
  const customerLoadNumber =
    load.customer?.customerDetails?.customerLoadNumber || load.customerLoad

  // Format carrier name (from load level or first dispatch)
  const carrierDisplay =
    formatCarrierName(load.carrier) ||
    formatCarrierName(load.dispatches?.[0]?.carrier) ||
    ''

  // Contact info. Like the web's ContactCard, the block shows when any field
  // has data and each field prints on its own, so a contact saved without a
  // name still shows its phone, title or email. A phone with no digits counts
  // as empty, as the web hides it, so it cannot leave a bare 'Contact' heading.
  const contact = load.contact
  const contactTelephone = formatPhoneNumber(contact?.telephone)
  const hasContact = !!(
    contact?.name ||
    contact?.title ||
    contactTelephone ||
    contact?.email
  )

  // Freight details
  const freightType = load.freightDetails?.freightType || ''
  const commodity = formatCommodity(load.freightDetails?.commodity)
  const temperature = formatTemperature(load.freightDetails)

  // Requirements
  const requirements = load.requirements
  const requirementsParts: string[] = []
  if (requirements?.trailerSize)
    requirementsParts.push(requirements.trailerSize)
  if (requirements?.trailerType)
    requirementsParts.push(requirements.trailerType)
  if (requirements?.team) {
    const teamValue = requirements.team.toLowerCase()
    if (teamValue === 'solo') requirementsParts.push('Solo')
    else if (teamValue === 'team') requirementsParts.push('Team')
    else if (teamValue === 'team & solo') requirementsParts.push('Team & Solo')
  }
  const requirementsDisplay = requirementsParts.join(' | ')

  // Rate details
  const rateInfo = formatRateDetailsSummary(load.rateDetails)
  const totalRate = calculateTotalRate(load.rateDetails)

  // Sales person
  const salesPersonDisplay = formatSalesPerson(load.salesPersonDetails)

  // Load note
  const loadNote = load.loadNote

  // Phone/email handlers
  // Use window.top to escape iframe context for tel:/mailto: links
  const handlePhoneClick = (phone: string) => {
    try {
      if (window.top) {
        window.top.location.href = `tel:${phone}`
      } else {
        window.open(`tel:${phone}`, '_blank')
      }
    } catch {
      window.open(`tel:${phone}`, '_blank')
    }
  }
  const handleEmailClick = (email: string) => {
    try {
      if (window.top) {
        window.top.location.href = `mailto:${email}`
      } else {
        window.open(`mailto:${email}`, '_blank')
      }
    } catch {
      window.open(`mailto:${email}`, '_blank')
    }
  }

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Load Number and Status */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Load #{load.loadNumber}
        </Typography>
        <Chip
          label={load.status?.replace(/-/g, ' ').toUpperCase()}
          color={getStatusColor(load.status)}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.65rem' }}
        />
      </Box>

      {/* First Pickup & Last Drop Summary */}
      {(firstPickupSchedule || lastDropSchedule) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
            p: 1.5,
            bgcolor: 'grey.50',
            borderRadius: 1
          }}
        >
          {firstPickupSchedule && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <PickupIcon
                sx={{ fontSize: 16, color: 'primary.main', mt: 0.25 }}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.2 }}
                >
                  First Pickup
                  {firstPickupLocation && ` • ${firstPickupLocation}`}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {firstPickupSchedule}
                </Typography>
              </Box>
            </Box>
          )}
          {lastDropSchedule && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DropIcon sx={{ fontSize: 16, color: 'error.main', mt: 0.25 }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.2 }}
                >
                  Last Drop
                  {lastDropLocation && ` • ${lastDropLocation}`}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {lastDropSchedule}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Customer info */}
      {customerDisplay !== '-' && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Customer
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {customerDisplay}
          </Typography>
          {customerLoadNumber && (
            <Typography variant="caption" color="text.secondary">
              Ref: {customerLoadNumber}
            </Typography>
          )}
        </Box>
      )}

      {/* Carrier info */}
      {carrierDisplay && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Carrier
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {carrierDisplay}
          </Typography>
        </Box>
      )}

      {/* Contact info */}
      {hasContact && contact && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Contact
          </Typography>
          {contact.name && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {contact.name}
            </Typography>
          )}
          {contact.title && (
            <Typography variant="body2">Title: {contact.title}</Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
              flexWrap: 'wrap'
            }}
          >
            {contactTelephone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => handlePhoneClick(contact.telephone!)}
                  sx={{ p: 0.25 }}
                >
                  <PhoneIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  {contactTelephone}
                </Typography>
              </Box>
            )}
            {contact.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => handleEmailClick(contact.email!)}
                  sx={{ p: 0.25 }}
                >
                  <EmailIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                </IconButton>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {contact.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Freight Details */}
      {(freightType || commodity || temperature) && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Freight Details
          </Typography>
          {freightType && (
            <Typography variant="body2">
              <strong>Type:</strong> {freightType}
            </Typography>
          )}
          {commodity && (
            <Typography variant="body2">
              <strong>Commodity:</strong> {commodity}
            </Typography>
          )}
          {temperature && (
            <Typography variant="body2">
              <strong>Temperature:</strong> {temperature}
            </Typography>
          )}
        </Box>
      )}

      {/* Requirements */}
      {requirementsDisplay && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Equipment
          </Typography>
          <Typography variant="body2">{requirementsDisplay}</Typography>
        </Box>
      )}

      {/* Rate Details */}
      {totalRate > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Rate
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {rateInfo.main}
          </Typography>
          {rateInfo.additional.length > 0 && (
            <Box sx={{ mt: 0.5, pl: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Additional Charges:
              </Typography>
              {rateInfo.additional.map((charge, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  sx={{ display: 'block', pl: 1 }}
                >
                  {charge}
                </Typography>
              ))}
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Total:</strong> {formatCurrency(totalRate)}
          </Typography>
        </Box>
      )}

      {/* Sales Person */}
      {salesPersonDisplay && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.25 }}
          >
            Sales Person
          </Typography>
          <Typography variant="body2">{salesPersonDisplay}</Typography>
        </Box>
      )}

      {/* Load Note */}
      {loadNote && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.25 }}
            >
              Load Note
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                fontSize: '0.8rem'
              }}
            >
              {loadNote}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}

export default LoadSummary
